import React, { useState } from 'react';
import { Box, Grid, SelectChangeEvent, Skeleton } from '@mui/material';
import { Button } from 'src/components/new/Button';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BkToolTip from 'src/components/tooltip/BkToolTip';
import { Typography } from 'src/components/new/Typography';
import { TextField } from 'src/components/new/inputs/TextField';
import Select from 'src/components/new/inputs/Select';
import { apiInstance } from 'src/api/apiInstance';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';

interface estimatePriceData {
  pagesPerMailing: number;
  service: 'rush' | 'normal';
  firstClass: number;
  certified: number;
  priorityExpress: number;
  mexico: number;
  canada: number;
  international: number;
  internationalPriorityExpress: number;
}

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

const initialFormData: Record<string, string> = {
  pagesPerMailing: '',
  service: '',
  firstClass: '',
  certified: '',
  priorityExpress: '',
  mexico: '',
  canada: '',
  international: '',
  internationalPriorityExpress: '',
};

const CostEstimator = () => {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  const [calculatedEstimations, setCalculatedEstimations] = useState({
    documentProcessing: 0,
    uspsPostage: 0,
    total: 0,
  });

  const estimationFields = [
    { label: 'Pages per mailing', name: 'pagesPerMailing', required: true },
    { label: 'Service', name: 'service', required: true },
    { label: 'First-Class', name: 'firstClass' },
    { label: 'Certified', name: 'certified' },
    { label: 'Priority Express', name: 'priorityExpress' },
    { label: 'Mexico', name: 'mexico' },
    { label: 'Canada', name: 'canada' },
    { label: 'International', name: 'international' },
    { label: 'International Priority Express', name: 'internationalPriorityExpress' },
  ];

  const collectPriceEstimation = async (data: estimatePriceData) => {
    try {
      setIsLoading(true);
      const response = await apiInstance.post('/job/estimate-price', data);
      const calculatedEstimations = response.data.data;
      setCalculatedEstimations({
        documentProcessing: calculatedEstimations.documentProcessing,
        uspsPostage: calculatedEstimations.uspsPostage,
        total: calculatedEstimations.total,
      });
    } catch (error: any) {
      setIsLoading(false);
      setShowSnackbarMsg({
        type: 'error',
        msg: 'Something went wrong',
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleChange = (event: React.ChangeEvent<HTMLInputElement> | SelectChangeEvent) => {
    const { name, value } = event.target;
    if (name !== 'service' && !/^\d*$/.test(value)) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleClear = () => {
    setFormData(initialFormData);
    setErrors({});
  };

  const handleSubmit = () => {
    const newErrors: Record<string, string> = {};
    estimationFields
      .filter((field) => field.required)
      .forEach(({ name, label }) => {
        if (!formData[name]) {
          newErrors[name] = `${label} is required`;
        }
      });
    if (
      !formData.firstClass &&
      !formData.canada &&
      !formData.certified &&
      !formData.international &&
      !formData.internationalPriorityExpress &&
      !formData.mexico &&
      !formData.priorityExpress
    ) {
      newErrors['atLeastOne'] = 'Please enter at least one recipient!';
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      collectPriceEstimation({
        pagesPerMailing: Number(formData.pagesPerMailing ?? 0),
        service: formData.service === 'Rush' ? 'rush' : 'normal',
        firstClass: Number(formData.firstClass ?? 0),
        certified: Number(formData.certified ?? 0),
        priorityExpress: Number(formData.priorityExpress ?? 0),
        internationalPriorityExpress: Number(formData.internationalPriorityExpress ?? 0),
        international: Number(formData.international ?? 0),
        canada: Number(formData.canada ?? 0),
        mexico: Number(formData.mexico ?? 0),
      });
    }
  };

  return (
    <Box sx={{ px: 4, py: 3, display: 'flex', gap: 15 }}>
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
      <Box sx={{ width: '50%' }}>
        <Typography variant="h4" sx={{ fontSize: '3.25rem', fontFamily: 'ABC Arizona Mix', fontWeight: 300 }}>
          Price Estimator
        </Typography>
        <Box>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Job Details
          </Typography>
          <Grid container spacing={3} mb={2}>
            <Grid item xs={6}>
              <TextField
                error={errors.pagesPerMailing}
                id="pagesPerMailing"
                label="Pages Per Mailing"
                inputProps={{ maxLength: 10 }}
                required
                name="pagesPerMailing"
                value={formData.pagesPerMailing}
                placeholder="Add..."
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={6}>
              <Select
                tooltip={
                  <BkToolTip
                    Icon={InfoOutlinedIcon}
                    title="If you require same-day service for a job submitted after 12 pm PST, select Rush. For all other jobs, select Normal."
                  />
                }
                disabled={false}
                name="service"
                value={formData.service}
                placeholder="Choose"
                data={['Normal', 'Rush']}
                label="Service"
                required
                onChange={handleChange}
                error={errors.service}
              />
            </Grid>
          </Grid>
          <Box display="flex" alignItems="center" gap={1} my={2}>
            <Typography variant="h6">Recipients By Mail Class*</Typography>
            <BkToolTip
              Icon={InfoOutlinedIcon}
              title="Please add the total number of mailings from each USPS postage type. Domestic First-Class postage is default."
            />
          </Box>
          <Grid container spacing={2} mb={3}>
            {estimationFields.slice(2).map(({ label, name }) => (
              <Grid item xs={4} key={name}>
                <TextField
                  label={label}
                  name={name}
                  hideHelperText
                  inputProps={{ maxLength: 10 }}
                  removeSpacer
                  value={formData[name]}
                  placeholder="Add..."
                  onChange={handleChange}
                  id={name}
                />
              </Grid>
            ))}
          </Grid>
          {errors.atLeastOne && (
            <Typography sx={{ marginTop: '0.625rem', color: '#AC3737', marginBottom: '0.625rem' }}>
              {errors.atLeastOne}
            </Typography>
          )}
          <Box display="flex" gap={2} mb={2}>
            <Button disabled={isLoading} type="primary" onClick={handleSubmit} sx={{ flex: 3 }}>
              Calculate
            </Button>
            <Button
              type="secondary"
              onClick={handleClear}
              sx={{ flex: 1, backgroundColor: 'transparent', border: 'none', color: '#436eb2' }}
            >
              Clear All
            </Button>
          </Box>
          <Typography sx={{ fontStyle: 'italic', fontSize: '0.75rem' }}>
            Sales tax and/or credit card convenience fees may apply.
          </Typography>
        </Box>
      </Box>
      <Box pt={8} mb={3}>
        <Typography variant="h6">Estimation</Typography>
        <Box
          sx={{
            padding: '0.9375rem',
            paddingRight: '0.9375rem',
            border: '0.0625rem solid #cac4d0',
            borderRadius: '0rem',
            marginTop: '0.9375rem',
            width: '25rem',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3125rem' }}>
            <Typography fontSize={15}>Document Processing</Typography>
            {isLoading ? (
              <Skeleton width={100} />
            ) : (
              <Typography fontSize={15}>${calculatedEstimations.documentProcessing.toFixed(2)}</Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.9375rem' }}>
            <Typography fontSize={15}>Postage & Delivery</Typography>
            {isLoading ? (
              <Skeleton width={100} />
            ) : (
              <Typography fontSize={15}>${calculatedEstimations.uspsPostage.toFixed(2)}</Typography>
            )}
          </Box>
          <hr style={{ border: 'none', backgroundColor: '#ece9dc', height: '0.125rem' }} />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '0.3125rem',
            }}
          >
            <Typography fontSize={20} fontWeight={500}>
              Total
            </Typography>
            {isLoading ? (
              <Skeleton width={100} />
            ) : (
              <Typography fontSize={20} fontWeight={500}>
                ${(calculatedEstimations.documentProcessing + calculatedEstimations.uspsPostage).toFixed(2)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CostEstimator;
